import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Multi Builder filters modal. Mirrors the Snipe Feed `FiltersModal` for the
 * AFL `market_and_implied_ev` strategy with one important difference: the
 * single `minOdds` field is replaced with an `oddsRange` ({min, max}) field so
 * users can pin both ends of the price band they want in the table.
 *
 * The container, header, footer, switches, slider and chip primitives are all
 * deliberately kept visually identical to Snipe Feed's modal so the two share
 * the same affordances at a glance.
 */

const DEFAULTS = {
    minBookmakers: { enabled: true, value: 4 },
    minEv: { enabled: true, value: 10 },
    minImpliedEv: { enabled: false, value: 0 },
    oddsRange: { enabled: false, value: { min: 1.50, max: 2.50 } },
    minSampleSize: { enabled: false, value: 5 },
    startWithin: { enabled: false, value: 3 },
    excludeBestBookmakers: { enabled: false, value: [] },
};

function SectionShell({ label, hint, enabled, onToggle, children }) {
    return (
        <section
            className={`relative rounded-lg border bg-slate-900/40 px-4 pt-3.5 pb-4 transition-colors ${
                enabled ? 'border-emerald-500/40' : 'border-slate-800/80 hover:border-slate-700/80'
            }`}
        >
            {enabled && (
                <span
                    aria-hidden="true"
                    className="absolute left-0 top-2 bottom-2 w-[2px] rounded-r bg-emerald-400"
                />
            )}
            <header className="mb-2.5 flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <h4 className="text-[10px] font-mono font-semibold text-slate-400 uppercase tracking-[0.14em]">
                        {label}
                    </h4>
                    {hint && (
                        <p className="mt-1 text-[11px] text-slate-500 leading-snug">{hint}</p>
                    )}
                </div>
                {typeof onToggle === 'function' && (
                    <button
                        type="button"
                        onClick={onToggle}
                        role="switch"
                        aria-checked={enabled}
                        aria-label={`Toggle ${label}`}
                        className={`relative inline-flex shrink-0 items-center rounded-full transition-colors mt-0.5 p-0 ${
                            enabled ? 'bg-emerald-500' : 'bg-slate-700'
                        }`}
                        style={{ width: '34px', height: '18px' }}
                    >
                        <span
                            aria-hidden="true"
                            className="block rounded-full bg-white shadow transition-transform duration-150"
                            style={{
                                width: '14px',
                                height: '14px',
                                transform: enabled ? 'translateX(18px)' : 'translateX(2px)',
                            }}
                        />
                    </button>
                )}
            </header>
            {enabled && children && (
                <div className="pt-1">{children}</div>
            )}
        </section>
    );
}

function ThresholdSlider({
    value,
    min,
    max,
    step = 1,
    onChange,
    formatValue,
    formatBound,
    valueLabel = 'Value',
}) {
    const display = formatValue ? formatValue(value) : Number(value);
    const minLabel = formatBound ? formatBound(min) : String(min);
    const maxLabel = formatBound ? formatBound(max) : String(max);

    return (
        <div>
            <div className="flex items-baseline justify-between mb-2">
                <span className="text-[10px] font-mono text-slate-600 uppercase tracking-widest">
                    {valueLabel}
                </span>
                <span className="text-xl font-mono font-bold text-emerald-400 tabular-nums leading-none">
                    {display}
                </span>
            </div>

            <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={value}
                onChange={(e) => onChange(Number(e.target.value))}
                className="w-full h-1.5 bg-slate-800 rounded-full appearance-none cursor-pointer accent-emerald-500
                    [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-emerald-400 [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-emerald-600 [&::-webkit-slider-thumb]:shadow-lg
                    [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-emerald-400 [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-emerald-600"
            />

            <div className="flex justify-between mt-1.5 text-[9px] font-mono text-slate-700 uppercase tracking-wider">
                <span>{minLabel}</span>
                <span>{maxLabel}</span>
            </div>
        </div>
    );
}

/**
 * Min/Max odds inputs. Renders the current band ("1.50 – 10.00") above a pair
 * of numeric inputs. Values are stored as raw strings while editing so users
 * can clear/type freely; we sanitise on blur and on apply.
 */
function OddsRangeInputs({ value, onChange }) {
    const minStr = value?.min ?? '';
    const maxStr = value?.max ?? '';
    const minNum = parseFloat(minStr);
    const maxNum = parseFloat(maxStr);
    const display = (!isNaN(minNum) && !isNaN(maxNum))
        ? `${Number(minNum).toFixed(2)} – ${Number(maxNum).toFixed(2)}`
        : '—';

    const sanitise = (raw, fallback) => {
        const n = parseFloat(raw);
        if (isNaN(n)) return fallback;
        return Math.max(1.01, n);
    };

    const handleMinBlur = () => {
        const min = sanitise(minStr, 1.50);
        const max = sanitise(maxStr, 2.50);
        onChange({ min, max: Math.max(max, min) });
    };
    const handleMaxBlur = () => {
        const min = sanitise(minStr, 1.50);
        const max = sanitise(maxStr, 2.50);
        onChange({ min: Math.min(min, max), max });
    };

    return (
        <div>
            <div className="flex items-baseline justify-between mb-2">
                <span className="text-[10px] font-mono text-slate-600 uppercase tracking-widest">
                    Odds Range
                </span>
                <span className="text-xl font-mono font-bold text-emerald-400 tabular-nums leading-none">
                    {display}
                </span>
            </div>

            <div className="flex items-center gap-2">
                <label className="flex-1 min-w-0">
                    <span className="block text-[9px] font-mono text-slate-600 uppercase tracking-widest mb-1">Min</span>
                    <input
                        type="text"
                        inputMode="decimal"
                        value={minStr}
                        onChange={(e) => onChange({ ...value, min: e.target.value })}
                        onBlur={handleMinBlur}
                        className="w-full px-2 py-1.5 bg-slate-950/60 border border-slate-800 rounded text-xs font-mono text-white text-right tabular-nums focus:outline-none focus:border-emerald-500"
                    />
                </label>
                <span aria-hidden="true" className="pt-4 text-slate-600 text-xs font-mono">–</span>
                <label className="flex-1 min-w-0">
                    <span className="block text-[9px] font-mono text-slate-600 uppercase tracking-widest mb-1">Max</span>
                    <input
                        type="text"
                        inputMode="decimal"
                        value={maxStr}
                        onChange={(e) => onChange({ ...value, max: e.target.value })}
                        onBlur={handleMaxBlur}
                        className="w-full px-2 py-1.5 bg-slate-950/60 border border-slate-800 rounded text-xs font-mono text-white text-right tabular-nums focus:outline-none focus:border-emerald-500"
                    />
                </label>
            </div>
        </div>
    );
}

function BookmakerChip({ bookmaker, label, logo, selected, onClick }) {
    return (
        <button
            type="button"
            onClick={() => onClick(bookmaker)}
            title={selected ? `Excluding ${label}` : label}
            className={`group inline-flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-[11px] font-medium transition-all duration-150 ${
                selected
                    ? 'border-rose-500/40 bg-rose-500/10 text-rose-300'
                    : 'border-slate-800 bg-slate-900/40 text-slate-400 hover:border-slate-700 hover:text-slate-200'
            }`}
        >
            {logo ? (
                <img
                    src={logo}
                    alt={label}
                    className={`w-3.5 h-3.5 rounded object-cover ${selected ? '' : 'opacity-60 group-hover:opacity-90'}`}
                />
            ) : (
                <span className="text-[11px]">{label}</span>
            )}
            <span>{label}</span>
            {selected && (
                <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <path d="M6 6l12 12M18 6L6 18" />
                </svg>
            )}
        </button>
    );
}

function MultiBuilderFiltersModal({
    filters,
    onApply,
    onClose,
    maxBookmakers,
    bookmakers,
    bookmakerLogos,
    bookmakerNames,
}) {
    const [localFilters, setLocalFilters] = useState(filters);

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        const onKey = (e) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => {
            document.body.style.overflow = '';
            window.removeEventListener('keydown', onKey);
        };
    }, [onClose]);

    const toggleField = (key) => () => {
        setLocalFilters((prev) => ({
            ...prev,
            [key]: { ...prev[key], enabled: !prev[key]?.enabled },
        }));
    };
    const setFieldValue = (key) => (value) => {
        setLocalFilters((prev) => ({
            ...prev,
            [key]: { ...prev[key], value },
        }));
    };

    const handleExcludeBookmakerClick = (bookmaker) => {
        setLocalFilters((prev) => {
            const current = prev.excludeBestBookmakers?.value || [];
            const updated = current.includes(bookmaker)
                ? current.filter((b) => b !== bookmaker)
                : [...current, bookmaker];
            return {
                ...prev,
                excludeBestBookmakers: { ...prev.excludeBestBookmakers, value: updated },
            };
        });
    };

    const handleApply = () => {
        // Sanitise odds range before applying so the consumer always gets numbers.
        const range = localFilters.oddsRange?.value || {};
        const minNum = parseFloat(range.min);
        const maxNum = parseFloat(range.max);
        const cleanMin = isNaN(minNum) ? 1.50 : Math.max(1.01, minNum);
        const cleanMaxRaw = isNaN(maxNum) ? 2.50 : Math.max(1.01, maxNum);
        const cleanMax = Math.max(cleanMin, cleanMaxRaw);
        onApply({
            ...localFilters,
            oddsRange: {
                ...localFilters.oddsRange,
                value: { min: cleanMin, max: cleanMax },
            },
        });
        onClose();
    };

    const handleReset = () => {
        setLocalFilters(DEFAULTS);
    };

    const activeCount = useMemo(() => {
        return Object.entries(localFilters).filter(([, f]) => {
            if (!f?.enabled) return false;
            if (Array.isArray(f.value)) return f.value.length > 0;
            return true;
        }).length;
    }, [localFilters]);

    return createPortal(
        <div
            className="fixed inset-0 flex items-end md:items-center justify-center p-0 md:p-6 bg-slate-950/80 backdrop-blur-sm animate-fade-in"
            style={{ zIndex: 10000 }}
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-label="Multi Builder Filters"
        >
            <div
                className="relative w-full md:max-w-xl bg-slate-900 border-t md:border border-slate-800 rounded-t-2xl md:rounded-2xl shadow-2xl shadow-black/60 overflow-hidden flex flex-col max-h-[85vh] md:max-h-[90vh] animate-slide-up md:animate-fade-in"
                onClick={(e) => e.stopPropagation()}
                style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 0px)' }}
            >
                {/* Mobile grab handle */}
                <div aria-hidden="true" className="md:hidden flex justify-center pt-2">
                    <span className="block w-10 h-1 rounded-full bg-slate-700/80" />
                </div>

                {/* Top emerald hairline + subtle glow (desktop only) */}
                <div className="hidden md:block absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-emerald-500/60 to-transparent" />
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-40 h-20 bg-emerald-500/10 blur-3xl pointer-events-none" />

                {/* Header */}
                <div className="relative flex items-center justify-between px-5 pt-3 md:pt-5 pb-4 border-b border-slate-800/80">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-slate-800/60 border border-slate-700/50 flex items-center justify-center text-emerald-400">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3 5h18M6 12h12M10 19h4" />
                            </svg>
                        </div>
                        <div>
                            <h3 className="text-sm font-semibold text-white tracking-tight">Filters</h3>
                            <p className="text-[10px] font-mono text-slate-500 uppercase tracking-[0.14em]">
                                {activeCount > 0 ? `${activeCount} active` : 'Defaults'}
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close filters"
                        className="w-8 h-8 rounded-md flex items-center justify-center text-slate-500 hover:text-white hover:bg-slate-800/70 transition-colors"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6L6 18" />
                        </svg>
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-auto styled-scrollbar px-5 py-5 space-y-4">
                    {/* Min Bookmakers */}
                    <SectionShell
                        label="Minimum Bookmakers"
                        hint="Only show legs priced by at least this many bookmakers."
                        enabled={!!localFilters.minBookmakers?.enabled}
                        onToggle={toggleField('minBookmakers')}
                    >
                        <ThresholdSlider
                            valueLabel="Min Books"
                            value={localFilters.minBookmakers?.value ?? 3}
                            min={2}
                            max={Math.max(2, maxBookmakers || 12)}
                            step={1}
                            onChange={setFieldValue('minBookmakers')}
                            formatValue={(v) => Math.round(Number(v))}
                            formatBound={(v) => Math.round(Number(v))}
                        />
                    </SectionShell>

                    {/* Min Edge% */}
                    <SectionShell
                        label="Minimum Edge%"
                        hint="Only show legs with at least this edge over the market price."
                        enabled={!!localFilters.minEv?.enabled}
                        onToggle={toggleField('minEv')}
                    >
                        <ThresholdSlider
                            valueLabel="Min Edge"
                            value={localFilters.minEv?.value ?? 10}
                            min={10}
                            max={20}
                            step={1}
                            onChange={setFieldValue('minEv')}
                            formatValue={(v) => `+${Math.round(Number(v))}%`}
                            formatBound={(v) => `${v}%`}
                        />
                    </SectionShell>

                    {/* Min Implied EV */}
                    <SectionShell
                        label="Minimum Implied EV%"
                        hint="Only show legs with at least this implied expected value."
                        enabled={!!localFilters.minImpliedEv?.enabled}
                        onToggle={toggleField('minImpliedEv')}
                    >
                        <ThresholdSlider
                            valueLabel="Min Implied EV"
                            value={localFilters.minImpliedEv?.value ?? 0}
                            min={-20}
                            max={50}
                            step={1}
                            onChange={setFieldValue('minImpliedEv')}
                            formatValue={(v) => `${Number(v) >= 0 ? '+' : ''}${Math.round(Number(v))}%`}
                            formatBound={(v) => `${v >= 0 ? '+' : ''}${v}%`}
                        />
                    </SectionShell>

                    {/* Min Sample Size */}
                    <SectionShell
                        label="Minimum Game Sample"
                        hint="Only show legs with at least this many games of hit-rate data."
                        enabled={!!localFilters.minSampleSize?.enabled}
                        onToggle={toggleField('minSampleSize')}
                    >
                        <ThresholdSlider
                            valueLabel="Min Games"
                            value={localFilters.minSampleSize?.value ?? 1}
                            min={1}
                            max={30}
                            step={1}
                            onChange={setFieldValue('minSampleSize')}
                            formatValue={(v) => Math.round(Number(v))}
                            formatBound={(v) => Math.round(Number(v))}
                        />
                    </SectionShell>

                    {/* Odds Range (replaces Snipe Feed's single Min Odds) */}
                    <SectionShell
                        label="Odds Range"
                        hint="Only show legs whose best price falls inside this band."
                        enabled={!!localFilters.oddsRange?.enabled}
                        onToggle={toggleField('oddsRange')}
                    >
                        <OddsRangeInputs
                            value={localFilters.oddsRange?.value || { min: 1.50, max: 2.50 }}
                            onChange={setFieldValue('oddsRange')}
                        />
                    </SectionShell>

                    {/* Match Start Time */}
                    <SectionShell
                        label="Match Start Time"
                        hint="Only show games starting within this time window."
                        enabled={!!localFilters.startWithin?.enabled}
                        onToggle={toggleField('startWithin')}
                    >
                        <ThresholdSlider
                            valueLabel="Starts Within"
                            value={localFilters.startWithin?.value ?? 3}
                            min={0.5}
                            max={5}
                            step={0.5}
                            onChange={setFieldValue('startWithin')}
                            formatValue={(v) => {
                                const n = Number(v);
                                if (n < 1) return `${Math.round(n * 60)}m`;
                                return `${n}h`;
                            }}
                            formatBound={(v) => (v < 1 ? `${Math.round(v * 60)}m` : `${v}h`)}
                        />
                    </SectionShell>

                    {/* Exclude Best Bookmakers */}
                    <SectionShell
                        label="Exclude Best Bookmakers"
                        hint="Hide legs where the best odds come from a selected bookmaker."
                        enabled={!!localFilters.excludeBestBookmakers?.enabled}
                        onToggle={toggleField('excludeBestBookmakers')}
                    >
                        {bookmakers && bookmakers.length > 0 && (
                            <>
                                <div className="flex flex-wrap gap-1.5">
                                    {bookmakers.map((bk) => (
                                        <BookmakerChip
                                            key={bk}
                                            bookmaker={bk}
                                            label={bookmakerNames?.[bk] || bk}
                                            logo={bookmakerLogos?.[bk]}
                                            selected={(localFilters.excludeBestBookmakers?.value || []).includes(bk)}
                                            onClick={handleExcludeBookmakerClick}
                                        />
                                    ))}
                                </div>
                                {(localFilters.excludeBestBookmakers?.value || []).length > 0 && (
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setLocalFilters((prev) => ({
                                                ...prev,
                                                excludeBestBookmakers: { ...prev.excludeBestBookmakers, value: [] },
                                            }))
                                        }
                                        className="mt-3 text-[10px] font-mono text-slate-500 hover:text-slate-300 uppercase tracking-widest"
                                    >
                                        Clear ({localFilters.excludeBestBookmakers.value.length})
                                    </button>
                                )}
                            </>
                        )}
                    </SectionShell>
                </div>

                {/* Footer */}
                <div className="relative flex items-center justify-between gap-3 px-5 py-3.5 border-t border-slate-800/80 bg-slate-950/40">
                    <button
                        type="button"
                        onClick={handleReset}
                        className="text-[11px] font-mono text-slate-500 hover:text-slate-200 uppercase tracking-widest transition-colors py-2 md:py-0"
                    >
                        Reset
                    </button>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 md:px-3.5 md:py-1.5 text-xs font-medium text-slate-300 rounded-md border border-slate-700/80 hover:bg-slate-800/60 hover:border-slate-600 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleApply}
                            className="px-4 py-2 md:px-4 md:py-1.5 text-xs font-semibold text-emerald-950 bg-emerald-400 rounded-md hover:bg-emerald-300 transition-colors shadow-[0_0_0_1px_rgba(16,185,129,0.4)]"
                        >
                            Apply Filters
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}

export { DEFAULTS as MULTI_BUILDER_DEFAULT_FILTERS };
export default MultiBuilderFiltersModal;
